// src/services/restaurant/RestaurantReportService.js
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { companyInfo } from '../../utils/dataInfo';

// Helper functions
const formatCurrency = (amount) => {
return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD'
}).format(amount || 0);
};

const formatNumber = (num) => {
return new Intl.NumberFormat('en-US').format(num || 0);
};

const formatDate = (date) => {
if (!date) return 'N/A';
return new Date(date).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
});
};

// Generate Excel Report
export const exportRestaurantReportToExcel = async (reportData, filters = {}) => {
try {
    const workbook = XLSX.utils.book_new();
    
    // Summary Sheet
    const summarySheetData = [
    ['RESTAURANT STOCK REPORT SUMMARY'],
    ['Generated on', new Date().toLocaleString()],
    [''],
    ['Total Items', reportData.summary?.total_items || 0],
    ['Total Balance', reportData.summary?.total_balance || 0],
    ['Active Items', reportData.summary?.active_items || 0],
    ['Inactive Items', reportData.summary?.inactive_items || 0],
    [''],
    ['DATE RANGE'],
    ['Oldest Stock', reportData.summary?.oldest_stock ? formatDate(reportData.summary.oldest_stock) : 'N/A'],
    ['Newest Stock', reportData.summary?.newest_stock ? formatDate(reportData.summary.newest_stock) : 'N/A']
    ];

    const summarySheet = XLSX.utils.aoa_to_sheet(summarySheetData);
    XLSX.utils.book_append_sheet(workbook, summarySheet, 'Summary');

    // Details Sheet
    const detailsHeaders = [
    'Item Name',
    'Balance',
    'UOM',
    'Status',
    'Stock Date',
    'Description'
    ];

    const detailsData = reportData.details?.map(item => [
    item.name,
    item.balance || 0,
    item.uom,
    item.status === 1 ? 'Active' : 'Inactive',
    formatDate(item.stock_date),
    item.description || ''
    ]) || [];

    const detailsSheetData = [detailsHeaders, ...detailsData];
    const detailsSheet = XLSX.utils.aoa_to_sheet(detailsSheetData);
    XLSX.utils.book_append_sheet(workbook, detailsSheet, 'Details');

    // Trends Sheet
    if (reportData.trends?.length > 0) {
    const trendsHeaders = ['Month', 'Item Count', 'Total Balance'];
    const trendsData = reportData.trends.map(trend => [
        trend.month,
        trend.item_count,
        trend.total_balance || 0
    ]);
    
    const trendsSheetData = [trendsHeaders, ...trendsData];
    const trendsSheet = XLSX.utils.aoa_to_sheet(trendsSheetData);
    XLSX.utils.book_append_sheet(workbook, trendsSheet, 'Trends');
    }

    // Top Items Sheet
    if (reportData.topItems?.length > 0) {
    const topItemsHeaders = ['Rank', 'Item Name', 'Balance', 'UOM'];
    const topItemsData = reportData.topItems.map((item, index) => [
        index + 1,
        item.name,
        item.balance || 0,
        item.uom
    ]);
    
    const topItemsSheetData = [topItemsHeaders, ...topItemsData];
    const topItemsSheet = XLSX.utils.aoa_to_sheet(topItemsSheetData);
    XLSX.utils.book_append_sheet(workbook, topItemsSheet, 'Top Items');
    }

    // Set column widths
    const sheets = workbook.SheetNames;
    sheets.forEach(sheetName => {
    const worksheet = workbook.Sheets[sheetName];
    if (worksheet['!ref']) {
        const range = XLSX.utils.decode_range(worksheet['!ref']);
        const colWidths = [];
        
        for (let col = range.s.c; col <= range.e.c; col++) {
        let maxWidth = 0;
        for (let row = range.s.r; row <= range.e.r; row++) {
            const cell = worksheet[XLSX.utils.encode_cell({ r: row, c: col })];
            if (cell && cell.v) {
            const cellWidth = String(cell.v).length;
            maxWidth = Math.max(maxWidth, cellWidth);
            }
        }
        colWidths.push({ wch: Math.min(maxWidth + 2, 50) });
        }
        
        worksheet['!cols'] = colWidths;
    }
    });

    // Generate file name
    const fileName = `restaurant_stock_report_${new Date().toISOString().split('T')[0]}.xlsx`;
    XLSX.writeFile(workbook, fileName);

    return { success: true, fileName };
} catch (error) {
    console.error('Restaurant Report Excel export failed:', error);
    throw new Error('Failed to export to Excel: ' + error.message);
}
};

// Generate PDF Report
export const exportRestaurantReportToPDF = async (reportData, filters = {}) => {
try {
    const doc = new jsPDF('landscape');
    
    // Set document properties
    doc.setProperties({
    title: 'Restaurant Stock Analysis Report',
    subject: 'Comprehensive Restaurant Stock Inventory Report',
    author: companyInfo.name,
    keywords: 'restaurant, stock, inventory, report, analysis',
    creator: companyInfo.name
    });

    // Title Page
    doc.setFontSize(24);
    doc.setTextColor(33, 150, 243);
    doc.text('RESTAURANT STOCK ANALYSIS REPORT', 105, 50, { align: 'center' });
    
    doc.setFontSize(16);
    doc.setTextColor(66, 66, 66);
    doc.text(companyInfo.name, 105, 65, { align: 'center' });
    
    doc.setFontSize(12);
    doc.setTextColor(97, 97, 97);
    doc.text(`Generated on: ${new Date().toLocaleString()}`, 105, 75, { align: 'center' });
    
    if (filters.dateFrom && filters.dateTo) {
    doc.text(`Period: ${formatDate(filters.dateFrom)} to ${formatDate(filters.dateTo)}`, 105, 82, { align: 'center' });
    }

    // Summary Section
    doc.addPage();
    doc.setFontSize(18);
    doc.setTextColor(33, 150, 243);
    doc.text('EXECUTIVE SUMMARY', 20, 20);

    doc.setFontSize(11);
    doc.setTextColor(66, 66, 66);
    
    let yPos = 35;
    const summaryItems = [
    ['Total Items:', formatNumber(reportData.summary?.total_items || 0)],
    ['Total Balance:', formatNumber(reportData.summary?.total_balance || 0)],
    ['Active Items:', formatNumber(reportData.summary?.active_items || 0)],
    ['Inactive Items:', formatNumber(reportData.summary?.inactive_items || 0)]
    ];

    summaryItems.forEach(([label, value], index) => {
    const x = index % 2 === 0 ? 20 : 110;
    const y = yPos + Math.floor(index / 2) * 10;
    
    doc.setTextColor(97, 97, 97);
    doc.text(label, x, y);
    doc.setTextColor(33, 33, 33);
    doc.text(value, x + 50, y);
    });

    // Detailed Stock Analysis Table
    doc.addPage();
    doc.setFontSize(18);
    doc.setTextColor(33, 150, 243);
    doc.text('DETAILED STOCK ANALYSIS', 20, 20);

    const tableData = reportData.details?.map(item => [
    item.name,
    formatNumber(item.balance || 0),
    item.uom,
    item.status === 1 ? 'Active' : 'Inactive',
    formatDate(item.stock_date)
    ]) || [];

    autoTable(doc, {
    startY: 30,
    head: [['Item Name', 'Balance', 'UOM', 'Status', 'Stock Date']],
    body: tableData,
    theme: 'striped',
    headStyles: { fillColor: [33, 150, 243], textColor: 255 },
    columnStyles: {
        0: { cellWidth: 'auto' },
        1: { halign: 'right' },
        2: { halign: 'center' },
        3: { halign: 'center' },
        4: { halign: 'center' }
    },
    didDrawCell: (data) => {
        // Color code status
        if (data.column.index === 3) {
        if (data.cell.raw === 'Active') {
            doc.setTextColor(76, 175, 80);
        } else {
            doc.setTextColor(244, 67, 54);
        }
        }
        
        // Color code balance
        if (data.column.index === 1) {
        const balance = parseFloat(data.cell.raw);
        if (balance === 0) {
            doc.setTextColor(244, 67, 54);
        } else if (balance < 10) {
            doc.setTextColor(255, 152, 0);
        } else {
            doc.setTextColor(33, 33, 33);
        }
        }
    }
    });

    // Top Items Page
    if (reportData.topItems?.length > 0) {
    doc.addPage();
    doc.setFontSize(18);
    doc.setTextColor(33, 150, 243);
    doc.text('TOP ITEMS BY BALANCE', 20, 20);

    const topItemsData = reportData.topItems.slice(0, 10).map((item, index) => [
        index + 1,
        item.name,
        formatNumber(item.balance || 0),
        item.uom
    ]);

    autoTable(doc, {
        startY: 30,
        head: [['Rank', 'Item Name', 'Balance', 'UOM']],
        body: topItemsData,
        theme: 'grid',
        headStyles: { fillColor: [76, 175, 80], textColor: 255 }
    });
    }

    // Trends Page
    if (reportData.trends?.length > 0) {
    doc.addPage();
    doc.setFontSize(18);
    doc.setTextColor(33, 150, 243);
    doc.text('STOCK TRENDS (LAST 6 MONTHS)', 20, 20);

    const trendsData = reportData.trends.map(trend => [
        trend.month,
        formatNumber(trend.item_count || 0),
        formatNumber(trend.total_balance || 0)
    ]);

    autoTable(doc, {
        startY: 30,
        head: [['Month', 'Item Count', 'Total Balance']],
        body: trendsData,
        theme: 'striped',
        headStyles: { fillColor: [156, 39, 176], textColor: 255 }
    });
    }

    // Footer on all pages
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFontSize(10);
    doc.setTextColor(128, 128, 128);
    doc.text(`Page ${i} of ${pageCount}`, doc.internal.pageSize.width - 30, doc.internal.pageSize.height - 10);
    doc.text(companyInfo.name, 20, doc.internal.pageSize.height - 10);
    }

    const fileName = `restaurant_stock_report_${new Date().toISOString().split('T')[0]}.pdf`;
    doc.save(fileName);

    return { success: true, fileName };
} catch (error) {
    console.error('Restaurant Report PDF export failed:', error);
    throw new Error('Failed to export to PDF: ' + error.message);
}
};

// Generate CSV Report
export const exportRestaurantReportToCSV = async (reportData, filters = {}) => {
try {
    // Prepare CSV data
    const csvLines = [];
    
    // Header
    csvLines.push('RESTAURANT STOCK ANALYSIS REPORT');
    csvLines.push(`Generated on: ${new Date().toLocaleString()}`);
    csvLines.push(`Company: ${companyInfo.name}`);
    csvLines.push('');
    
    // Summary
    csvLines.push('SUMMARY');
    csvLines.push('Metric,Value');
    csvLines.push(`Total Items,${reportData.summary?.total_items || 0}`);
    csvLines.push(`Total Balance,${reportData.summary?.total_balance || 0}`);
    csvLines.push(`Active Items,${reportData.summary?.active_items || 0}`);
    csvLines.push(`Inactive Items,${reportData.summary?.inactive_items || 0}`);
    csvLines.push('');
    
    // Details
    csvLines.push('DETAILED STOCK ITEMS');
    csvLines.push('Item Name,Balance,UOM,Status,Stock Date,Description');
    
    reportData.details?.forEach(item => {
    csvLines.push([
        `"${item.name.replace(/"/g, '""')}"`,
        item.balance || 0,
        `"${item.uom.replace(/"/g, '""')}"`,
        item.status === 1 ? 'Active' : 'Inactive',
        formatDate(item.stock_date),
        `"${(item.description || '').replace(/"/g, '""')}"`
    ].join(','));
    });
    
    csvLines.push('');
    
    // Top Items
    if (reportData.topItems?.length > 0) {
    csvLines.push('TOP ITEMS BY BALANCE');
    csvLines.push('Rank,Item Name,Balance,UOM');
    
    reportData.topItems.slice(0, 10).forEach((item, index) => {
        csvLines.push([
        index + 1,
        `"${item.name.replace(/"/g, '""')}"`,
        item.balance || 0,
        `"${item.uom.replace(/"/g, '""')}"`
        ].join(','));
    });
    }

    // Create CSV file
    const csvContent = csvLines.join('\n');
    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `restaurant_stock_report_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    return { success: true };
} catch (error) {
    console.error('Restaurant Report CSV export failed:', error);
    throw new Error('Failed to export to CSV: ' + error.message);
}
};

// Simple PDF Export (Quick Report)
export const exportRestaurantReportSimplePDF = async (reportData) => {
try {
    const doc = new jsPDF();
    
    // Title
    doc.setFontSize(18);
    doc.text('Restaurant Stock Quick Report', 20, 20);
    
    // Summary
    doc.setFontSize(12);
    let y = 40;
    
    const quickSummary = [
    `Total Items: ${formatNumber(reportData.summary?.total_items || 0)}`,
    `Total Balance: ${formatNumber(reportData.summary?.total_balance || 0)}`,
    `Active Items: ${formatNumber(reportData.summary?.active_items || 0)}`,
    `Inactive Items: ${formatNumber(reportData.summary?.inactive_items || 0)}`
    ];
    
    quickSummary.forEach(line => {
    doc.text(line, 20, y);
    y += 10;
    });
    
    y += 10;
    
    // Critical Items (Zero Balance)
    const zeroBalanceItems = reportData.details?.filter(item => item.balance === 0) || [];
    if (zeroBalanceItems.length > 0) {
    doc.text('Out of Stock Items:', 20, y);
    y += 10;
    
    zeroBalanceItems.slice(0, 5).forEach(item => {
        doc.text(`• ${item.name}`, 25, y);
        y += 7;
    });
    }
    
    const fileName = `restaurant_stock_quick_report_${new Date().toISOString().split('T')[0]}.pdf`;
    doc.save(fileName);
    
    return { success: true, fileName };
} catch (error) {
    console.error('Restaurant Report Simple PDF export failed:', error);
    throw new Error('Failed to export quick PDF: ' + error.message);
}
};